import { useCallback, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { PROBE_INTERVAL_MS } from '@netwatch/shared';
import type { Heartbeat, Outage, Rollup, Sample } from '@netwatch/shared';
import { Card, Heatmap, LineChart } from './charts.js';
import {
  CLASS_META,
  RANGES,
  RAW_RANGE_MS,
  downsample,
  fetchHeartbeats,
  fetchOutages,
  fetchRollups,
  fetchSamples,
  formatDuration,
  heatmapCells,
  liveStatus,
  outageStats,
  rollupHeatmapCells,
  rollupPoints,
  speedPoints,
  summaryFor,
  summaryFromRollups,
  type RangeKey,
} from './data.js';
import { auth, signIn, signOutNow } from './firebase.js';

const STALE_MS = PROBE_INTERVAL_MS * 3 + 60_000;

function Badge({ classification }: { classification: keyof typeof CLASS_META }) {
  const meta = CLASS_META[classification];
  // Icon + label alongside the colour — a status colour never carries meaning alone.
  return (
    <span className="badge" style={{ color: meta.color }}>
      <span aria-hidden>{meta.icon}</span>
      {meta.label}
    </span>
  );
}

function Tile({ label, value, note, unknown }: { label: string; value: string; note?: string; unknown?: boolean }) {
  return (
    <div className="card tile">
      <h2>{label}</h2>
      <div className={unknown ? 'value unknown' : 'value'}>{value}</div>
      {note && <div className="note">{note}</div>}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [range, setRange] = useState<RangeKey>('24h');
  const [samples, setSamples] = useState<Sample[]>([]);
  const [rollups, setRollups] = useState<Rollup[]>([]);
  const [outages, setOutages] = useState<Outage[]>([]);
  const [heartbeats, setHeartbeats] = useState<Heartbeat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => onAuthStateChanged(auth, (u) => {
    setUser(u);
    setAuthReady(true);
  }), []);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);

    const to = Date.now();
    const from = to - RANGES[range];
    // Beyond 24h the raw samples exceed Firestore's 10k query cap — and only
    // live 14 days regardless — so longer ranges read the hourly rollups.
    const useRaw = RANGES[range] <= RAW_RANGE_MS;

    // allSettled, not all: these are four independent reads, and one failing
    // must not blank the three that succeeded. A single bad query previously
    // took the whole page down, including the live status banner.
    const [s, r, o, h] = await Promise.allSettled([
      useRaw ? fetchSamples(from, to) : Promise.resolve([]),
      useRaw ? Promise.resolve([]) : fetchRollups(from, 'hour'),
      fetchOutages(from),
      fetchHeartbeats(),
    ]);

    const problems: string[] = [];
    const take = <T,>(res: PromiseSettledResult<T>, label: string, fallback: T): T => {
      if (res.status === 'fulfilled') return res.value;
      problems.push(`${label}: ${(res.reason as Error).message}`);
      return fallback;
    };

    setSamples(take(s, 'samples', [] as Sample[]));
    setRollups(take(r, 'rollups', [] as Rollup[]));
    setOutages(take(o, 'outages', [] as Outage[]));
    setHeartbeats(take(h, 'heartbeats', [] as Heartbeat[]));
    setNow(to);
    setError(problems.length ? problems.join(' · ') : null);
    setLoading(false);
  }, [range, user]);

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 60_000);
    return () => clearInterval(id);
  }, [load]);

  const from = now - RANGES[range];
  const useRaw = RANGES[range] <= RAW_RANGE_MS;
  const summary = useMemo(
    () => (useRaw ? summaryFor(samples, from, now) : summaryFromRollups(rollups, from, now)),
    [useRaw, samples, rollups, from, now],
  );
  const stats = useMemo(() => outageStats(outages), [outages]);
  const openOutage = outages.find((o) => o.endTs === null);
  const status = useMemo(() => liveStatus(heartbeats, samples, openOutage, now, STALE_MS), [heartbeats, samples, openOutage, now]);

  if (!authReady) return <div className="wrap">Loading…</div>;

  if (!user) {
    return (
      <div className="wrap">
        <h1>netwatch</h1>
        <p style={{ color: 'var(--text-secondary)' }}>Home internet history. Sign in to continue.</p>
        <button className="ghost" onClick={() => void signIn()}>
          Sign in with Google
        </button>
      </div>
    );
  }

  // Raw samples for 24h; hourly rollups beyond that. The rollups carry the same
  // metrics pre-aggregated, so the charts read identically either way.
  const latency = useRaw ? downsample(samples, (s) => s.rttMedianMs, from, now) : rollupPoints(rollups, (r) => r.rttP50Ms);
  const jitterSeries = useRaw ? downsample(samples, (s) => s.jitterMs, from, now) : rollupPoints(rollups, (r) => r.jitterMeanMs);
  const loss = useRaw ? downsample(samples, (s) => s.loss * 100, from, now) : rollupPoints(rollups, (r) => r.lossMean * 100);
  const dns = useRaw ? downsample(samples, (s) => s.dnsMs, from, now) : rollupPoints(rollups, (r) => r.dnsP50Ms);
  const downSpeed = useRaw ? speedPoints(samples, 'downMbps') : rollupPoints(rollups, (r) => r.downMbpsMax);
  const upSpeed = useRaw ? speedPoints(samples, 'upMbps') : rollupPoints(rollups, (r) => r.upMbpsMax);
  const heatCells = useRaw ? heatmapCells(samples, (s) => s.rttMedianMs) : rollupHeatmapCells(rollups, (r) => r.rttP50Ms);
  const rollupsPending = !useRaw && rollups.length === 0;

  return (
    <div className="wrap" style={{ opacity: loading && samples.length > 0 ? 0.65 : 1, transition: 'opacity .15s' }}>
      <header className="top">
        <h1>netwatch</h1>
        <button className="ghost" onClick={() => void signOutNow()}>
          Sign out
        </button>
      </header>

      {/* Status banner: three-valued, because "no contact" is not "down". */}
      <div className="banner">
        {status.kind === 'up' && (
          <>
            <Badge classification="up" /> <strong>Internet is up.</strong>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>Last measured {formatDuration(now - status.since)} ago.</span>
          </>
        )}
        {status.kind === 'down' && (
          <>
            <Badge classification={status.classification} /> <strong>Down for {formatDuration(now - status.since)}.</strong>
          </>
        )}
        {status.kind === 'no-contact' && (
          <>
            <Badge classification="monitoring_gap" /> <strong>No contact with the prober for {formatDuration(now - status.lastSeen)}.</strong>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>
              The link may be down, or the monitoring box may be. Until its buffered readings arrive this is recorded as unmeasured, not as an outage.
            </span>
          </>
        )}
        {status.kind === 'never' && <>No prober has reported yet.</>}
      </div>

      {error && (
        <div className="banner" style={{ borderColor: 'var(--status-critical)' }}>
          <strong>Couldn’t load part of the data.</strong> <span style={{ color: 'var(--text-secondary)' }}>{error}</span>
        </div>
      )}

      {rollupsPending && (
        <div className="banner">
          <strong>No hourly summaries yet.</strong>{' '}
          <span style={{ color: 'var(--text-secondary)' }}>
            Ranges beyond 24 hours are built from hourly summaries, which are written once an hour. Until the first one lands this range has
            nothing to show — try “Last 24 hours”.
          </span>
        </div>
      )}

      {/* One filter row above everything it scopes. */}
      <div className="filters">
        {(Object.keys(RANGES) as RangeKey[]).map((k) => (
          <button key={k} aria-pressed={range === k} onClick={() => setRange(k)}>
            {k === '24h' ? 'Last 24 hours' : k === '7d' ? 'Last 7 days' : 'Last 30 days'}
          </button>
        ))}
        <button className="muted-link" style={{ marginLeft: 'auto' }} onClick={() => setShowTable((v) => !v)}>
          {showTable ? 'Hide table view' : 'Show table view'}
        </button>
      </div>

      <div className="grid tiles">
        <Tile
          label="Uptime"
          value={summary.uptimePct === null ? 'No data' : `${summary.uptimePct.toFixed(2)}%`}
          unknown={summary.uptimePct === null}
          note="Of time actually measured"
        />
        <Tile
          label="Coverage"
          value={`${summary.coveragePct.toFixed(1)}%`}
          note={
            summary.coveragePct < 90
              ? `${formatDuration(summary.unmonitoredMs)} unmeasured — uptime is less certain`
              : 'How much of the window was measured'
          }
        />
        <Tile label="Outages" value={String(stats.count)} note={`${formatDuration(stats.totalMs)} total downtime`} />
        <Tile
          label="Longest outage"
          value={formatDuration(stats.longestMs)}
          note={stats.meanMs !== null ? `Average ${formatDuration(stats.meanMs)}` : 'None recorded'}
        />
      </div>

      <div className="grid">
        <Card
          title="Latency and jitter"
          subtitle="The everyday quality signal — far more telling than raw speed. Both in milliseconds, so they share one axis."
        >
          <LineChart
            series={[
              { label: 'Latency (median)', color: 'var(--series-1)', points: latency, unit: 'ms' },
              { label: 'Jitter', color: 'var(--series-2)', points: jitterSeries, unit: 'ms' },
            ]}
            yLabel="ms"
          />
        </Card>

        <Card title="Packet loss" subtitle="Share of probe targets that failed to answer. This is what breaks calls.">
          <LineChart series={[{ label: 'Loss', color: 'var(--series-1)', points: loss, unit: '%' }]} yLabel="%" height={150} />
        </Card>

        <Card title="DNS resolution time" subtitle="A common failure that isn’t an outage: everything hangs while the link is technically fine.">
          <LineChart series={[{ label: 'DNS', color: 'var(--series-1)', points: dns, unit: 'ms' }]} yLabel="ms" height={150} />
        </Card>

        <Card
          title="Speed"
          subtitle="Measured every few hours, on demand, and right after each recovery. Read these as a floor: short transfers on a fast line under-report."
        >
          <LineChart
            series={[
              // Sparse: speed runs every few hours, so most hourly rollups hold
              // no test at all. Without this the line breaks on every empty hour
              // and renders as disconnected stubs on any range beyond 24h.
              { label: 'Download', color: 'var(--series-1)', points: downSpeed, unit: ' Mbps', sparse: true },
              { label: 'Upload', color: 'var(--series-2)', points: upSpeed, unit: ' Mbps', sparse: true },
            ]}
            yLabel="Mbps"
          />
        </Card>

        <Card title="Latency by time of day" subtitle="Where evening congestion shows up. Unmeasured hours are left blank rather than shaded.">
          <Heatmap cells={heatCells} unit="ms" label="Median latency" />
        </Card>

        <Card title="Outage log" subtitle="Only genuine connectivity failures are listed. Unmeasured stretches belong to coverage, not here.">
          {outages.length === 0 ? (
            <p className="empty">Nothing recorded in this range.</p>
          ) : (
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Verdict</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {outages.map((o) => (
                    <tr key={o.id}>
                      <td>{new Date(o.startTs).toLocaleString()}</td>
                      <td>{o.endTs === null ? 'ongoing' : formatDuration(o.durationMs)}</td>
                      <td>
                        <Badge classification={o.classification} />
                      </td>
                      <td className="reason">{o.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Table-view twin: every charted value reachable without relying on colour. */}
        {showTable && (
          <Card title="Table view" subtitle="The same measurements as plain numbers.">
            <div className="scroll-x">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>State</th>
                    <th>Latency</th>
                    <th>Jitter</th>
                    <th>Loss</th>
                    <th>DNS</th>
                    <th>Down</th>
                    <th>Up</th>
                  </tr>
                </thead>
                <tbody>
                  {samples.slice(-200).reverse().map((s) => (
                    <tr key={s.ts}>
                      <td>{new Date(s.ts).toLocaleString()}</td>
                      <td>{!s.localOk ? 'house fault' : !s.wanOk ? 'down' : 'up'}</td>
                      <td>{s.rttMedianMs === null ? '—' : `${Math.round(s.rttMedianMs)} ms`}</td>
                      <td>{s.jitterMs === null ? '—' : `${Math.round(s.jitterMs)} ms`}</td>
                      <td>{`${Math.round(s.loss * 100)}%`}</td>
                      <td>{s.dnsMs === null ? '—' : `${Math.round(s.dnsMs)} ms`}</td>
                      <td>{s.speed?.downMbps ? `${s.speed.downMbps.toFixed(0)} Mbps` : '—'}</td>
                      <td>{s.speed?.upMbps ? `${s.speed.upMbps.toFixed(0)} Mbps` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <footer className="note">
        Uptime is calculated over measured time only. Stretches where the prober itself was unreachable are reported as coverage rather than
        counted as downtime — assuming otherwise is how uptime figures end up wrong.
      </footer>
    </div>
  );
}
